<template>
  <div id="gen-page-content-wrapper">
    <div class="gen-main">
      <nav class="gen-fixed-nav">
        <div class="gen-user-info">
          <span class="gen-user-avatar">
            <fa :icon="['fas', 'user']" />
          </span>
          <span class="gen-user-greeting">
            <span class="gen-user-greeting-label">Welcome back</span>
            <span class="gen-user-greeting-name">{{ user ? user.name : '' }}</span>
          </span>
        </div>
        <div class="gen-nav-actions">
          <button
          v-if="globalIsUserAllowed('Release_History')"
         class="gen-nav-btn"
          title="Release History"
          @click="$bvModal.show('genericReleaseModal')"
        >
          <fa :icon="['fas', 'history']" />
        </button>
          <button
            v-if="globalIsUserAllowed('Change_Password')"
            class="gen-nav-btn"
            :disabled="isTestAccount"
            :title="isTestAccount ? 'Cannot change password of test account' : 'Change Password'"
            @click="$bvModal.show('PTVChangePasswordModal')"
          >
            <fa :icon="['fas', 'key']" />
          </button>
          <button class="gen-nav-btn gen-signout-btn" title="Sign Out" @click="signOut">
            <fa :icon="['fas', 'sign-out-alt']" />
          </button>
        </div>
      </nav>

      <div class="container-fluid gen-dashboard-content" :class="{ 'gen-content--map-max': mapFullscreen }">
        <div class="row gen-dashboard-section my-4 no-gutters">
          <div class="left-col pr-3">
            <div class="chart-card mb-3">
              <div class="chart-card-body d-flex flex-column">
                <div class="gen-card-header-row">
                  <span class="gen-card-header-icon">
                    <fa :icon="['fas', 'chart-bar']"></fa>
                  </span>
                  <h4 class="gen-card-title-text">Mileage by Roadway Type</h4>
                </div>
                <div class="chart-container flex-grow-1">
                  <apexchart
                    ref="genericReleasesChart"
                    type="bar"
                    width="100%"
                    height="100%"
                    :options="chartOptions"
                    :series="series"
                  />
                </div>
              </div>
            </div>

            <div class="releases-card mb-3">
              <div class="releases-card-body d-flex flex-column">
                <div class="gen-card-header-row">
                  <span class="gen-card-header-icon">
                    <fa :icon="['fas', 'tag']"></fa>
                  </span>
                  <h4 class="gen-card-title-text">Recent Releases</h4>
                </div>
                <vue-good-table
                  class="releases-table"
                  id="genericReleasesTable"
                  :rows="rows"
                  :columns="columns"
                  styleClass="vgt-table modern-table"
                >
                  <template #table-row="props">
                    <span v-if="props.column.field == 'release_date'">
                      {{ new Date(props.row.release_date).toLocaleDateString(language, { month: '2-digit', day: '2-digit', year: 'numeric' }) }}
                    </span>
                    <span v-else>
                      {{ props.row[props.column.field] }}
                    </span>
                  </template>
                </vue-good-table>
              </div>
            </div>

            <div class="total-mileage-strip">
              <div v-for="region in regions" :key="region.name" class="total-mileage-card">
                <div class="total-mileage-card-body">
                  <span class="total-mileage-region">{{ region.name }}</span>
                  <div class="total-mileage-display">
                    <span class="total-mileage-value">{{ region.count.toLocaleString() }}</span>
                    <span class="total-mileage-unit">mi</span>
                  </div>
                  <span class="total-mileage-label">Total Mileage</span>
                </div>
              </div>
            </div>
          </div>

          <div class="right-col pl-3">
            <div class="map-card h-100 w-100" :class="{ 'map-card--fullscreen': mapFullscreen }">
              <div class="map-card-header d-flex justify-content-between align-items-center">
                <div class="map-card-title">
                  <span class="map-card-title-icon">
                    <fa :icon="['fas', 'map']"></fa>
                  </span>
                  <span class="map-card-title-text">Map Overlays</span>
                </div>
                <button
                  class="map-fullscreen-btn"
                  :title="mapFullscreen ? 'Minimize map' : 'Expand map'"
                  :aria-label="mapFullscreen ? 'Minimize map' : 'Expand map'"
                  @click="toggleMapFullscreen"
                >
                  <fa :icon="['fas', mapFullscreen ? 'window-minimize' : 'expand']"></fa>
                </button>
              </div>
              <div class="map-body">
                <div class="h-100 w-100" id="vehiclesMap"></div>
                <div class="map-layer-panel" :class="{ 'map-layer-panel--minimized': layerPanelMinimized }">
                  <div class="map-layer-panel-header">
                    <h4 class="map-layer-panel-title">Layers</h4>
                    <button
                      class="map-layer-panel-minimize"
                      :aria-expanded="!layerPanelMinimized"
                      :title="layerPanelMinimized ? 'Expand layers' : 'Minimize layers'"
                      @click="layerPanelMinimized = !layerPanelMinimized"
                    >
                      <fa :icon="['fas', layerPanelMinimized ? 'chevron-up' : 'chevron-down']"></fa>
                    </button>
                  </div>
                  <template v-if="!layerPanelMinimized">
                    <draggable
                      v-model="layerList"
                      tag="div"
                      class="layer-toggle-list"
                      handle=".layer-drag-handle"
                    >
                      <div v-for="layer in layerList" :key="layer.name" class="layer-row">
                        <fa :icon="['fas', 'grip-vertical']" class="layer-drag-handle" title="Drag to reorder"></fa>
                        <span class="layer-row-label">{{ layer.display_name }}</span>
                        <button
                          class="layer-toggle-switch"
                          :class="{ 'is-on': layer.visible }"
                          :aria-pressed="layer.visible"
                          :title="layer.visible ? 'Hide layer' : 'Show layer'"
                          @click="$emit('toggle-layer', layer.name)"
                        >
                          <span class="layer-toggle-knob"></span>
                        </button>
                      </div>
                    </draggable>
                    <p class="text-muted mb-0" v-if="!layerList.length">
                      No layers loaded yet.
                    </p>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="gen-copyright">
        <a href="https://dmp-maps.com/legal/" class="gen-attribution" target="_blank">
          © {{ new Date().getFullYear() }} Dynamic Map Platform North America, Inc. All Rights Reserved
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>
#gen-page-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: #f8f9fa;
}

.gen-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.gen-fixed-nav {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 1.5rem;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid rgba(6, 60, 109, 0.08);
  box-shadow: 0 1px 12px rgba(6, 60, 109, 0.06);
}

.gen-user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #063c6d;
}

.gen-user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #0a548f 0%, #063c6d 100%);
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
  overflow: hidden;
  flex: 0 0 auto;
  color: #ffffff;
  font-size: 1rem;
}

.gen-nav-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.gen-nav-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.625rem;
  height: 2.625rem;
  border: 1px solid rgba(6, 60, 109, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.6);
  color: #4a74a8;
  font-size: 1.05rem;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.gen-nav-btn:hover {
  background: #ffffff;
  color: #063c6d;
  box-shadow: 0 4px 14px rgba(6, 60, 109, 0.14);
  transform: translateY(-1px);
}

.gen-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.gen-nav-btn:disabled:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #4a74a8;
  box-shadow: none;
  transform: none;
}

.gen-signout-btn:hover {
  background: #fff;
  color: #dc3545;
  box-shadow: 0 4px 14px rgba(220, 53, 69, 0.18);
}

.gen-user-greeting {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.gen-user-greeting-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: #8ab0cc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.gen-user-greeting-name {
  font-size: 1.02rem;
  font-weight: 700;
  color: #063c6d;
}

.container-fluid.gen-dashboard-content {
  position: relative; /* positioning context for the maximized map */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1.25rem 1.25rem 1.25rem;
}

/* When the map is maximized, lock the page scroll so the map stays put */
.container-fluid.gen-dashboard-content.gen-content--map-max {
  overflow: hidden;
}

.gen-dashboard-section {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.left-col,
.right-col {
  display: flex;
  flex-direction: column;
  flex: 1 1 50%;
  max-width: 50%;
  min-height: 0;
  min-width: 0;
}

/* ── Chart card ── */
.chart-card {
  flex: 1 1 auto;
  min-height: 16.25rem;
  background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(6, 60, 109, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chart-card-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.1rem 1.3rem;
}

.gen-card-header-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
}

.gen-card-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.1);
  color: #063c6d;
  font-size: 0.85rem;
}

.gen-card-title-text {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #063c6d;
  letter-spacing: 0.2px;
}

.chart-container {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
}

/* ── Releases card ── */
.releases-card {
  flex: 0 0 auto;
  background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(6, 60, 109, 0.07);
  overflow: hidden;
}

.releases-card-body {
  padding: 1.1rem 1.3rem;
}

.releases-table {
  flex: 0 0 auto;
}

.releases-table ::v-deep(.vgt-table.modern-table) {
  border: none;
  font-size: 0.85rem;
  background: transparent;
}

.releases-table ::v-deep(.vgt-table.modern-table th) {
  background-color: transparent;
  color: #8ab0cc;
  border: none;
  border-bottom: 1px solid rgba(6, 60, 109, 0.12);
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  padding: 0.5rem 0.75rem;
}

.releases-table ::v-deep(.vgt-table.modern-table td) {
  border: none;
  border-bottom: 1px solid rgba(6, 60, 109, 0.06);
  color: #063c6d;
  font-weight: 500;
  padding: 0.625rem 0.75rem;
  transition: background-color 0.12s ease;
}

.releases-table ::v-deep(.vgt-table.modern-table tr:last-child td) {
  border-bottom: none;
}

.releases-table ::v-deep(.vgt-table.modern-table tr:hover td) {
  background-color: rgba(6, 60, 109, 0.045);
}

/* ── Mileage strip ── */
.total-mileage-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.total-mileage-card {
  flex: 1 1 0;
  min-width: 8.125rem;
  background: linear-gradient(135deg, #ffffff 0%, #f4f8fb 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(6, 60, 109, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.total-mileage-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(6, 60, 109, 0.14);
}

.total-mileage-card-body {
  display: flex;
  flex-direction: column;
  padding: 0.85rem 1.1rem;
}

.total-mileage-region {
  font-size: 0.7rem;
  color: #8ab0cc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.total-mileage-display {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.total-mileage-value {
  font-size: 1.9rem;
  font-weight: 700;
  color: #063c6d;
  line-height: 1;
}

.total-mileage-unit {
  font-size: 0.9rem;
  color: #8ab0cc;
  font-weight: 500;
}

.total-mileage-label {
  font-size: 0.72rem;
  color: #9fb4c4;
  margin-top: 0.35rem;
}

/* ── Map card ── */
.map-card {
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(6, 60, 109, 0.08);
  overflow: hidden;
}

.map-card-header {
  flex: 0 0 auto;
  padding: 0.7rem 1.1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
  border-bottom: 1px solid rgba(6, 60, 109, 0.08);
}

.map-card-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.map-card-title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.1);
  color: #063c6d;
  font-size: 0.85rem;
}

.map-card-title-text {
  color: #063c6d;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.2px;
}

/* Fullscreen toggle button */
.map-fullscreen-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.08);
  color: #063c6d;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.map-fullscreen-btn:hover {
  background-color: #063c6d;
  color: #ffffff;
  transform: translateY(-1px);
}

/* Maximized: fill the dashboard content area while keeping the side menu
   and the top welcome nav visible (does not cover the whole viewport) */
.map-card--fullscreen {
  position: absolute;
  inset: 0.75rem 0.75rem 0.75rem 0.75rem;
  z-index: 1500;
  width: auto !important;
  height: auto !important;
}

.map-body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}

#vehiclesMap {
  height: 100%;
  width: 100%;
}

/* ── Map layer panel ── */
.map-layer-panel {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  z-index: 5;
  width: 16.125rem;
  max-height: 62%;
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(6, 60, 109, 0.18), 0 1px 2px rgba(6, 60, 109, 0.1);
  padding: 0.75rem 0.85rem;
  transition: max-height 0.2s ease, padding 0.2s ease;
}

.map-layer-panel--minimized {
  max-height: none;
  padding-bottom: 0.75rem;
}

.map-layer-panel-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.map-layer-panel:not(.map-layer-panel--minimized) .map-layer-panel-header {
  margin-bottom: 0.55rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(6, 60, 109, 0.1);
}

.map-layer-panel-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #063c6d;
  margin: 0;
}

.map-layer-panel-title::before {
  content: '';
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background-color: #1f9d55;
  box-shadow: 0 0 0 3px rgba(31, 157, 85, 0.18);
}

.map-layer-panel-minimize {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  background-color: rgba(6, 60, 109, 0.06);
  color: #063c6d;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.72rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.map-layer-panel-minimize:hover {
  background-color: rgba(6, 60, 109, 0.14);
}

.layer-toggle-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 13.75rem;
  overflow-y: auto;
  overflow-x: hidden;
}

.layer-toggle-list::-webkit-scrollbar {
  width: 5px;
  background-color: transparent !important;
}
.layer-toggle-list::-webkit-scrollbar-track {
  background-color: transparent !important;
  box-shadow: none !important;
}
.layer-toggle-list::-webkit-scrollbar-thumb {
  background-color: transparent !important;
  border-radius: 999px;
}
.map-layer-panel:hover .layer-toggle-list::-webkit-scrollbar-track {
  background-color: rgba(6, 60, 109, 0.06) !important;
  border-radius: 999px;
}
.map-layer-panel:hover .layer-toggle-list::-webkit-scrollbar-thumb {
  background-color: rgba(6, 60, 109, 0.25) !important;
}
.map-layer-panel:hover .layer-toggle-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(6, 60, 109, 0.5) !important;
}

.layer-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.375rem 0.5rem;
  border-radius: 8px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.layer-row:hover {
  background-color: rgba(255, 255, 255, 0.7);
  box-shadow: 0 1px 4px rgba(6, 60, 109, 0.08);
}

.layer-drag-handle {
  flex: 0 0 auto;
  color: #9fb3c8;
  cursor: grab;
  padding: 2px;
}

.layer-drag-handle:hover {
  color: #063c6d;
}

.layer-row:active .layer-drag-handle {
  cursor: grabbing;
}

.layer-row-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
  color: #063c6d;
  font-size: 0.9rem;
}

.layer-toggle-switch {
  position: relative;
  flex: 0 0 auto;
  width: 2.375rem;
  height: 1.25rem;
  border: none;
  border-radius: 999px;
  background-color: #cbd5e1;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s ease;
}

.layer-toggle-switch.is-on {
  background-color: #1f9d55;
  box-shadow: 0 0 0 3px rgba(31, 157, 85, 0.15);
}

.layer-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.layer-toggle-switch.is-on .layer-toggle-knob {
  left: 1.25rem;
}

/* ── Footer ── */
.gen-copyright {
  position: fixed;
  bottom: 0.625rem;
  right: 0.75rem;
  z-index: 5;
}

.gen-attribution {
  display: inline-flex;
  align-items: center;
  padding: 0.3125rem 0.875rem;
  color: #063c6d !important;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-decoration: none !important;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  border: none;
  border-radius: 999px;
  transition: color 0.15s ease, background 0.15s ease;
}

.gen-attribution:hover {
  color: #063c6d !important;
  background: rgba(255, 255, 255, 0.8);
  text-decoration: none !important;
}
</style>

<script>
import axios from 'axios'
import 'ol/ol.css'
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable'

export default {
  name: 'Dashboard',
  components: { draggable },
  computed: {
    ...mapGetters(['globalUserRole', 'globalAllowedUserModules', 'globalIsUserAllowed']),
    isTestAccount() {
      return this.user && this.user.test
    },
    // Two-way bridge to the parent's layer list — the getter reads the prop,
    // the setter (fired when draggable reorders) hands the new order back up.
    layerList: {
      get() {
        return this.layers
      },
      set(val) {
        this.$emit('reorder-layers', val)
      }
    }
  },
  data() {
    return {
      regions: [
        { name: 'North America', count: 0 },
        { name: 'Europe', count: 0 },
        { name: 'Middle East', count: 0 },
        { name: 'Japan', count: 0 }
      ],
      vehicleMap: null,
      mapFullscreen: false,
      layerPanelMinimized: false,
      series: [
        { name: 'Motorway', data: [3200, 4100, 3800, 2900] },
        { name: 'Trunk', data: [2500, 3600, 3400, 2100] },
        { name: 'Primary', data: [4100, 4800, 4200, 3000] },
        { name: 'Secondary', data: [3000, 3900, 3500, 2400] }
      ],
      chartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        dataLabels: { enabled: false },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: ['North America', 'Europe', 'Japan', 'Middle East'],
          title: { text: 'Region' }
        },
        yaxis: {
          title: { text: 'Miles' },
          labels: {
            formatter: val => {
              const orig = Math.pow(val, 3)
              if (orig >= 1e9) return (orig / 1e9).toFixed(1) + 'B'
              if (orig >= 1e6) return (orig / 1e6).toFixed(1) + 'M'
              if (orig >= 1e3) return (orig / 1e3).toFixed(1) + 'K'
              return Math.floor(orig).toString()
            }
          }
        },
        fill: { opacity: 1 },
        legend: { position: 'top' },
        tooltip: {
          y: {
            formatter: val => {
              const orig = Math.pow(val, 3)
              if (orig >= 1e9) return `${(orig / 1e9).toFixed(2)}B mi`
              if (orig >= 1e6) return `${(orig / 1e6).toFixed(2)}M mi`
              if (orig >= 1e3) return `${(orig / 1e3).toFixed(2)}K mi`
              return `${Math.floor(orig).toLocaleString()} mi`
            }
          }
        }
      },
      rows: [
        { region: 'North America', release_date: new Date() },
        { region: 'Europe', release_date: new Date() },
        { region: 'Japan', release_date: new Date() },
        { region: 'Middle East', release_date: new Date() }
      ],
      columns: [
        { label: 'Region', field: 'region', sortable: true },
        { label: 'Release Date', field: 'release_date', sortable: true }
      ],
      language: navigator.language
    }
  },
  props: {
    user: { type: [Object, null], default: null },
    mapsMode: { type: Boolean, default: false },
    layers: { type: Array, default: () => [] }
  },
  mounted() {
    this.$nextTick(() => {
      this.getDashboardStats()
      this.getReleaseDates()
    })
    window.addEventListener('keydown', this.handleMapFullscreenKey)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleMapFullscreenKey)
  },
  methods: {
    toggleMapFullscreen() {
      this.mapFullscreen = !this.mapFullscreen
      // The OpenLayers map lives in the parent (Generic.vue); ask it to re-measure
      // once the card has resized.
      this.$nextTick(() => {
        setTimeout(() => this.$emit('mapResize'), 60)
      })
    },
    handleMapFullscreenKey(e) {
      if (e.key === 'Escape' && this.mapFullscreen) {
        this.toggleMapFullscreen()
      }
    },
    signOut() {
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('pid')
      this.$emit('clearCurrentPositionsInterval')
      this.$router.push({ name: 'Login' })
    },
    getDashboardStats() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/drt/getGenericDashboardStats', {
          headers: { 'x-auth-token': sessionStorage.token }
        })
        .then(response => {
          let content = response.data

          this.regions = content.map(d => ({
            name: this.mapRegionName(d.region),
            count: Math.floor(d.total_mileage)
          }))

          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              categories: content.map(d => this.mapRegionName(d.region)),
              title: { text: 'Region' }
            }
          }

          const types = ['motorway', 'trunk', 'primary', 'secondary']
          this.series = types.map(type => ({
            name: this.capitalizeFirstLetter(type),
            data: content.map(d => Math.cbrt(d[`${type}_mileage`] || 0))
          }))
        })
        .catch(error => console.log(error))
      } else {
        console.log('No User Token')
      }
    },
    getReleaseDates() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/drt/getGenericReleaseDates', {
          headers: { 'x-auth-token': sessionStorage.token }
        })
        .then(response => {
          let content = response.data
          this.rows = content.map(d => ({
            region: this.mapRegionName(d.region),
            release_date: new Date(d.release_date)
          }))
        })
        .catch(error => console.log(error))
      } else {
        console.log('No User Token')
      }
    },
    mapRegionName(code) {
      switch (code) {
        case 'NA': return 'North America'
        case 'EU': return 'Europe'
        case 'JP': return 'Japan'
        case 'ME': return 'Middle East'
        default: return code
      }
    },
    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    }
  }
}
</script>
